/**
 * CartContext.jsx — Production-grade cart state
 *
 * KEY FIX vs previous version:
 *   openCart() no longer calls _closeWishlist().
 *
 *   The old code called _closeWishlist() (which called the wishlist's onClose())
 *   inside openCart(). WishlistDrawer.handleMoveToCart() calls openCart() after
 *   a successful add. That triggered onClose() on the wishlist, which in some
 *   React Router setups re-evaluates the current route and fires a navigation,
 *   sending the user to the product page.
 *
 *   Both drawers can be open simultaneously — the z-index stack handles layering.
 *   If you want mutual exclusion, manage it from the parent Layout component
 *   where both drawer states live, not from inside CartContext.
 *
 * CONTRACT (strictly enforced):
 *   addToCart  → returns undefined on success, throws Error on real failure
 *   fetchCart  → never throws to callers, retries internally, sets loading
 *   removeFromCart / updateQuantity → optimistic, revert on failure
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from '../config/api';
import { calculateShipping } from '../config/shipping';

// ─── Constants ────────────────────────────────────────────────────────────────
const CartContext = createContext(null);
const FETCH_TIMEOUT_MS = 30_000; // Allow for backend cold start (e.g. Render)
const MAX_RETRIES      = 2;

// ─── Pure helpers ─────────────────────────────────────────────────────────────
function calcShipping(_subtotal) {
  return calculateShipping(_subtotal);
}

function getSessionId() {
  let id = localStorage.getItem('cart_session_id');
  if (!id) {
    id = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem('cart_session_id', id);
  }
  return id;
}

function buildHeaders(token) {
  const h = {
    'Content-Type': 'application/json',
    'x-session-id': getSessionId(),
  };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

function mapItem(raw) {
  const productId =
    raw.product?._id
      ? String(raw.product._id)
      : raw.product
        ? String(raw.product)
        : raw.productId
          ? String(raw.productId)
          : null;
  // Ensure _id is always a string so remove/update can match and API URL is valid
  const lineId = raw._id != null ? String(raw._id) : null;

  return {
    _id:       lineId,
    id:        productId,
    productId,
    name:      raw.name  || 'Product',
    price:     Number(raw.priceAtTime ?? raw.price) || 0,
    image:     raw.image || '/hero.png',
    size:      raw.size  || 'Free Size',
    color:     raw.color || null,
    quantity:  Number(raw.quantity) || 1,
    slug:      raw.product?.slug ?? productId ?? '',
  };
}

// Backend success() puts payload in data.data; fetchCart normalizes to data.cart. Accept both.
function applyCartResponse(data, setCartItems, setCartSummary) {
  const cart = data.cart ?? data.data ?? {};
  const items = (cart.items ?? []).map(mapItem).filter((i) => i.productId);
  setCartItems(items);

  // Prefer computed subtotal from items when API subtotal is missing or zero but we have items (avoids ₹0 bug)
  const computedSubtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const subtotal = (cart.subtotal != null && cart.subtotal > 0) ? cart.subtotal : computedSubtotal;
  const discountAmount = cart.discountAmount ?? cart.coupon?.discountAmount ?? 0;
  const shipping = cart.shippingFee ?? calcShipping(subtotal);
  const total    = (cart.total != null && cart.total > 0)
    ? cart.total
    : Math.max(0, subtotal + shipping - discountAmount);
  const couponCode = cart.coupon?.code || null;

  setCartSummary({
    subtotal,
    shippingFee: shipping,
    total,
    discountAmount,
    couponCode,
  });

  return items;
}

// ─── Provider ─────────────────────────────────────────────────────────────────
export function CartProvider({ children }) {
  const { isAuthenticated, token, isAuthLoading } = useAuth();

  const [cartItems,   setCartItems]   = useState([]);
  const [isCartOpen,  setIsCartOpen]  = useState(false);
  const [cartSummary, setCartSummary] = useState({
    subtotal: 0, shippingFee: 0, total: 0, discountAmount: 0, couponCode: null,
  });
  const [loading, setLoading] = useState(true);

  // When user signs out, clear cart state immediately so UI shows empty
  const prevAuthRef = useRef(isAuthenticated);
  useEffect(() => {
    if (prevAuthRef.current === true && isAuthenticated === false) {
      setCartItems([]);
      setCartSummary({ subtotal: 0, shippingFee: 0, total: 0, discountAmount: 0, couponCode: null });
      sessionStorage.setItem('cart_count', '0');
    }
    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Stable mounted flag — survives retries unlike a local `let cancelled`
  const mountedRef  = useRef(true);
  const fetchingRef = useRef(false); // prevent overlapping fetches

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── fetchCart ──────────────────────────────────────────────────────────────
  const fetchCart = useCallback(async (attempt = 0, options = {}) => {
    const { silent = false } = options;
    if (!mountedRef.current) return;
    if (fetchingRef.current && attempt === 0) return; // debounce parallel calls
    fetchingRef.current = true;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort('timeout'), FETCH_TIMEOUT_MS);

    if (mountedRef.current && !silent) setLoading(true);

    try {
      const res = await fetch(`${API_URL}/cart`, {
        headers: buildHeaders(token),
        signal:  controller.signal,
      });
      clearTimeout(timer);
      if (!mountedRef.current) return;

      // 401 = expired token → continue as guest with empty cart
      if (res.status === 401) {
        setCartItems([]);
        setCartSummary({ subtotal: 0, shippingFee: 0, total: 0, discountAmount: 0, couponCode: null });
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      if (!mountedRef.current) return;

      if (data.success) {
        // The success helper wraps data in 'data' field: { success: true, data: { items, subtotal, ... } }
        // But applyCartResponse expects: { cart: { items, subtotal, ... } }
        // So we need to map data.data to data.cart
        const cartData = {
          success: true,
          cart: data.data || data.cart || {},
        };
        
        const items = applyCartResponse(cartData, setCartItems, setCartSummary);

        // Surface stock/price issues as warnings only
        cartData.cart?.issues?.forEach((issue) => {
          console.warn('[Cart] Issue:', issue);
        });

        // Track count in sessionStorage (resets on tab close, avoids false "expired" warnings)
        const prev = Number(sessionStorage.getItem('cart_count') ?? 0);
        if (prev > 0 && items.length === 0) {
          console.warn('[Cart] Cart was cleared during this session (stock/TTL).');
        }
        sessionStorage.setItem('cart_count', String(items.length));
      }
    } catch (err) {
      clearTimeout(timer);
      if (!mountedRef.current) return;

      const timedOut = err.name === 'AbortError' || controller.signal.aborted;

      if (!timedOut && attempt < MAX_RETRIES) {
        const delay = Math.min(1_000 * 2 ** attempt, 8_000);
        setTimeout(() => {
          if (mountedRef.current) fetchCart(attempt + 1, options);
        }, delay);
        return;
      }
      // Don't surface — show empty cart, don't block UI
      console.error('[Cart] fetchCart failed after retries:', err.message);
    } finally {
      fetchingRef.current = false;
      if (mountedRef.current && !silent) setLoading(false);
    }
  }, [token]); // token is the only real dep; API_URL is a module constant

  useEffect(() => {
    if (isAuthLoading) return;
    // After sign out we cleared cart_session_id; fetchCart will create a new session and get empty cart
    fetchCart(0);
  }, [isAuthenticated, isAuthLoading, fetchCart]);

  // ── addToCart ──────────────────────────────────────────────────────────────
  // CONTRACT: returns undefined on success, throws on failure
  // options: { size, color?, quantity, openDrawer? }
  const addToCart = useCallback(async (product, options = {}) => {
    const productId = product._id || product.id;
    if (!productId) throw new Error('Product ID missing — cannot add to cart.');

    const { size, color, quantity = 1, openDrawer = true } = options;
    if (!size) throw new Error('Please select a size.');

    const rawImg  = product.images?.[0];
    const image   = typeof rawImg === 'string' ? rawImg : rawImg?.url || rawImg?.secure_url || '/hero.png';
    const tempItem = {
      id:        String(productId),
      productId: String(productId),
      name:      product.name || product.title || 'Product',
      price:     product.price ?? 0,
      image,
      size,
      quantity,
      slug:      product.slug || String(productId),
    };
    if (color) tempItem.color = color;

    setCartItems((prev) => {
      const idx = prev.findIndex(
        (i) =>
          i.productId === tempItem.productId &&
          i.size === size &&
          (color ? (i.color || '') === color : true)
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [...prev, tempItem];
    });

    let res;
    try {
      res = await fetch(`${API_URL}/cart`, {
        method:  'POST',
        headers: buildHeaders(token),
        body: JSON.stringify({
          productId: String(productId),
          size:      String(size),
          quantity:  Math.max(1, parseInt(quantity, 10) || 1),
          ...(color ? { color: String(color).trim() } : {}),
        }),
      });
    } catch (networkErr) {
      setTimeout(() => { if (mountedRef.current) fetchCart(0); }, 0);
      throw new Error('Network error — please check your connection.');
    }

    if (!res.ok) {
      setTimeout(() => { if (mountedRef.current) fetchCart(0); }, 0);
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || `Could not add to bag (${res.status}).`);
    }

    try {
      const data = await res.json();
      const cartPayload = data?.cart ?? data?.data;
      if (cartPayload && mountedRef.current) {
        applyCartResponse({ cart: cartPayload }, setCartItems, setCartSummary);
      }
    } catch {
      setTimeout(() => { if (mountedRef.current) fetchCart(0); }, 0);
    }

    /* Optional drawer open on add (callers can disable for PLP quick-add).
     * Core Web Vitals — INP: defer opening drawer to next frame so click response paints first (≤200ms). */
    if (openDrawer && typeof window !== 'undefined') {
      const open = () => setIsCartOpen(true);
      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(open);
      } else {
        setTimeout(open, 0);
      }
    }
  }, [token, fetchCart]);

  // ── removeFromCart ─────────────────────────────────────────────────────────
  // Index-based removal so we only ever remove ONE line (avoids clearing whole cart when _id was missing)
  const removeFromCart = useCallback(async (itemId, size, color = null) => {
    const index = cartItems.findIndex(
      (i) =>
        i._id === itemId ||
        (String(i._id) === String(itemId)) ||
        (i.productId === itemId && i.size === size && (color != null ? (i.color || '') === color : true))
    );
    if (index === -1) return;
    const item = cartItems[index];

    setCartItems((prev) => prev.filter((_, i) => i !== index));

    const lineId = item._id != null ? String(item._id) : null;
    if (!lineId) {
      if (mountedRef.current) fetchCart(0);
      return;
    }

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

      const res = await fetch(`${API_URL}/cart/item/${lineId}`, {
        method:  'DELETE',
        headers: buildHeaders(token),
        signal:  controller.signal,
      });
      clearTimeout(timer);

      if (res.ok) {
        const data = await res.json().catch(() => null);
        if (data?.success && mountedRef.current) {
          applyCartResponse(data, setCartItems, setCartSummary);
        }
      } else {
        if (mountedRef.current) fetchCart(0);
      }
    } catch {
      if (mountedRef.current) fetchCart(0);
    }
  }, [cartItems, token, fetchCart]);

  // ── updateQuantity ─────────────────────────────────────────────────────────
  // Index-based update so we only change ONE line; API uses line item _id
  const updateQuantity = useCallback(async (itemId, size, newQty, color = null) => {
    if (newQty <= 0) { removeFromCart(itemId, size, color); return; }

    const index = cartItems.findIndex(
      (i) =>
        i._id === itemId ||
        (String(i._id) === String(itemId)) ||
        (i.productId === itemId && i.size === size && (color != null ? (i.color || '') === color : true))
    );
    if (index === -1) return;
    const item = cartItems[index];

    setCartItems((prev) =>
      prev.map((i, idx) => (idx === index ? { ...i, quantity: newQty } : i))
    );

    const lineId = item._id != null ? String(item._id) : null;
    if (!lineId) {
      if (mountedRef.current) fetchCart(0);
      return;
    }

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

      const res = await fetch(`${API_URL}/cart/item/${lineId}`, {
        method:  'PUT',
        headers: buildHeaders(token),
        body:    JSON.stringify({ quantity: newQty }),
        signal:  controller.signal,
      });
      clearTimeout(timer);

      if (res.ok) {
        const data = await res.json().catch(() => null);
        if (data?.success && mountedRef.current) {
          applyCartResponse(data, setCartItems, setCartSummary);
        }
      } else {
        if (mountedRef.current) fetchCart(0);
      }
    } catch {
      if (mountedRef.current) fetchCart(0);
    }
  }, [cartItems, token, removeFromCart, fetchCart]);

  // ── Stable helpers ─────────────────────────────────────────────────────────
  const getCartCount = useCallback(
    () => cartItems.reduce((s, i) => s + i.quantity, 0),
    [cartItems]
  );

  // ── openCart ───────────────────────────────────────────────────────────────
  // FIX: Removed _closeWishlist() call.
  //
  // The old implementation called _closeWishlist() here, which invoked the
  // wishlist drawer's onClose() prop. In the sequence:
  //
  //   handleMoveToCart() → addToCart() succeeds → openCart() called
  //     → _closeWishlist() → wishlist onClose() fires
  //       → React Router re-evaluates state → navigates to product page
  //
  // Both drawers can coexist at the same z-index stack (cart is above wishlist).
  // If you need to close the wishlist when cart opens, do it in your Layout:
  //
  //   const handleOpenCart = () => {
  //     setWishlistOpen(false);
  //     setCartOpen(true);
  //   };
  //
  // Update cart from move-to-cart response
  // The wishlist moveToCart service returns: { cart: { items, subtotal, ... } }
  // applyCartResponse expects: { cart: { items, subtotal, ... } }
  const updateCartFromResponse = useCallback((cartData) => {
    if (!cartData) return;
    const responseData = { success: true, cart: cartData };
    applyCartResponse(responseData, setCartItems, setCartSummary);
  }, []);

  const openCart  = useCallback(() => setIsCartOpen(true),  []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  /** Clear cart locally after order success. Backend marks cart converted on verify. */
  const clearCart = useCallback(() => {
    setCartItems([]);
    setCartSummary({ subtotal: 0, shippingFee: 0, total: 0, discountAmount: 0 });
    sessionStorage.setItem('cart_count', '0');
  }, []);

  const refreshCart = useCallback(() => fetchCart(0), [fetchCart]);
  /** Refresh cart without showing loading state (e.g. after removing coupon on checkout). */
  const refreshCartSilent = useCallback(() => fetchCart(0, { silent: true }), [fetchCart]);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartSummary,
        isCartOpen,
        loading,
        addToCart,
        removeFromCart,
        updateQuantity,
        getCartCount,
        openCart,
        closeCart,
        clearCart,
        refreshCart,
        refreshCartSilent,
        updateCartFromResponse,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}
